import {testIdProps} from '@github-ui/test-id-props'
import {CheckIcon, XIcon} from '@primer/octicons-react'
import {Popover, type PopoverProps, Spinner} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import type {ComponentProps} from 'react'

import type {Status} from '../../hooks/use-api-request'
import {CommitState} from '../../hooks/use-autosave'
import {Resources} from '../../strings'
import styles from './state-style-decorators.module.css'

type ErrorStateProps = {
  message?: string
  caret?: PopoverProps['caret']
  testId?: string
}

const AlertStyledBox: React.FC<ComponentProps<'div'>> = props => <div className={styles.errorBox} {...props} />

const ErrorStyledPointerBox = ({caret, message}: ErrorStateProps) => (
  <Popover caret={caret} open>
    <Popover.Content className={styles.errorPointerBox}>{message}</Popover.Content>
  </Popover>
)

/*
 * A component to indicate failure of an action inline
 * Return a styled box indicating error
 *  - default message is 'Something went wrong' but it can be customized
 *  - the association with the input field is done through a caret, default positioned on the top-left
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  message = Resources.genericErrorMessage,
  caret,
  testId = 'error-state-message-box',
}) => {
  const CustomBox = caret ? ErrorStyledPointerBox : AlertStyledBox
  return (
    <>
      <div className={styles.flexSpacer} />
      <CustomBox caret={caret} {...testIdProps(testId)}>
        {message}
      </CustomBox>
      <div className={styles.flexSpacer} />
    </>
  )
}

/*
 * A component to indicate success of an action inline
 * Return a green checkbox indicating success
 */
export const SuccessState: React.FC = () => <Octicon icon={CheckIcon} className={styles.successIcon} />

interface RequestStateProps {
  status: Status['status'] | CommitState
}

/** Shows an icon that indicates the state of a request. */
export const RequestStateIcon = ({status}: RequestStateProps) => {
  switch (status) {
    case 'idle':
    case CommitState.None:
      return <></>
    case 'failed':
    case CommitState.Failed:
      return <Octicon icon={XIcon} className={styles.failureIcon} />
    case 'loading':
      return <Spinner size="small" />
    case 'succeeded':
    case CommitState.Successful:
      return <Octicon icon={CheckIcon} className={styles.completedIcon} />
  }
}
